import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CA_DATA } from "../../models/data.model";
import Chart from "chart.js/auto";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivityInfosService } from "../../../activity_infos/services/activity_infos.service";
import {
  CaCumuleDialogComponent,
  CaCumuleDialogData,
  CaCumuleScale,
} from "./ca-cumule-dialog.component";

@Component({
  selector: "vl-widget-ca-cumule",
  standalone: true,
  imports: [
    SharedModule,
    WidgetTitleComponent,
    WidgetPeriodFiltersComponent,
    ChipsComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-ca-cumule.component.html",
  styleUrl: "./widget-ca-cumule.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetCaCumuleComponent {
  data = input<CA_DATA>();
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  period = signal<string>("m");
  timeScale = signal<string>("YTD");
  canvasRef = viewChild<ElementRef>("cacumule");
  totalDisplay: string;
  elementRef = inject(ElementRef);
  private dialogsService = inject(DialogsService);
  private activityInfos = toSignal(inject(ActivityInfosService).currentActivityInfos$);

  chart: Chart;

  private get exerciceStart(): string | undefined {
    return this.activityInfos()?.dateDebutExercice ?? undefined;
  }

  private timeScaleLabelMap: Record<string, string> = {
    "1 j": "Dernier jour",
    "5 j": "5 derniers jours",
    "1 m": "Dernier mois",
    "6 m": "6 derniers mois",
    "YTD": "Depuis le début de l'année",
    "1 a": "12 derniers mois",
    "5 a": "5 dernières années",
    "Max": "Tout l'historique",
  };

  openFullList(): void {
    const raw = this.data()?.data;
    if (!raw?.length) return;

    const { data: filtered } = WidgetHelperService.filterByTimeScale(
      raw as any,
      this.timeScale(),
      this.exerciceStart,
    );

    const dialogData: CaCumuleDialogData = {
      rawData: filtered as CA_DATA["data"],
      initialPeriod: this.period() as CaCumuleScale,
      timeScaleLabel: this.timeScaleLabelMap[this.timeScale()] ?? this.timeScale(),
    };

    this.dialogsService.open(
      CaCumuleDialogComponent,
      "min(95vw, 960px)",
      "fit-content",
      dialogData,
    );
  }

  constructor() {
    effect(
      () => {
        const data = this.data();
        const canvas = this.canvasRef();
        this.period();
        this.timeScale();

        if (data && canvas) {
          this.buildChart();
        }
      },
      { allowSignalWrites: true },
    );
  }

  buildChart() {
    if (this.data()?.data.length > 0) {
      let data = WidgetHelperService.getCaCumuleChart(this.data()?.data, {
        timeScale: this.timeScale(),
        period: this.period(),
        exerciceStart: this.exerciceStart,
      });
      const labels = [];
      const montants = [];

      data = data.sort((a, b) => a.date.localeCompare(b.date));
      for (let i = 0; i < data.length; i++) {
        if (data[i].date && data[i].montant) {
          labels.push(data[i].date);
          montants.push(data[i].montant);
        }
      }

      if (montants.length) {
        this.totalDisplay =
          WidgetHelperService.formatNumeric(montants[montants.length - 1]) +
          " €";
      } else {
        this.totalDisplay = " - €";
      }
      if (this.chart) {
        this.chart.destroy();
      }
      // Créez un nouveau graphique
      const bgAlt = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-on-background-alt");
      const accent = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-accent");
      const onAccent = getComputedStyle(
        this.elementRef.nativeElement,
      ).getPropertyValue("--color-on-accent");

      this.chart = new Chart(this.canvasRef().nativeElement.getContext("2d"), {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              data: montants,
              borderColor: accent, // Couleur de la ligne (rose clair)
              backgroundColor: "rgba(0, 0, 0, 0)", // Transparent pour le fond
              pointBackgroundColor: accent, // Couleur de la ligne (rose clair)
              pointBorderColor: onAccent, // Couleur des bordures des points (violet foncé)
              borderWidth: 5, // Largeur des bordures des points
              pointBorderWidth: 3, // Largeur des bordures des points
              pointRadius: 7, // Taille des points
              fill: false, // Désactive le remplissage sous la ligne
              tension: 0.2, // Lissage de la courbe
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          responsive: true, // Assure que le chart est responsive
          maintainAspectRatio: false, // P
          scales: {
            y: {
              ticks: {
                color: bgAlt,
              },
            },
            x: {
              ticks: {
                color: bgAlt,
              },
            },
          },
        },
      });
    }
  }
}
